/**
 * Created by neo on 03.01.17.
 *
 * Plain Athlete type with standalone utility functions.
 * Migrated from MobX observable to plain TypeScript.
 */

import { Media } from '../Media/Media';
import { User } from '../User';
import { EMPTY_ARRAY } from '../../Utils/Constants';
import { Audited } from '../Audited';
import { GenderType } from './GenderType';
import { notUndefined } from '../../Utils/notUndefined';
import { HttpBackend } from '../../Services/Http/HttpBackend';

// Plain data type — no MobX, no methods, no computed getters
export type Athlete = Audited & {
  user: User;
  firstname?: string;
  lastname?: string;
  birthdate?: string;
  birthYear?: number;
  gender: GenderType;
  profilePicture?: Media;
  nickname?: string;
  tags: string[];
  allowsDataSharing: boolean;
  acceptsMarketing: boolean;
};

// ─── Utility functions (replace computed getters) ────────────────────────────

export function getAthleteFullName(a: Athlete): string {
  return a.firstname?.trim() ?? '';
}

export function getAthleteFullNameUpper(a: Athlete): string {
  return getAthleteFullName(a).toUpperCase();
}

export function getAthleteInitials(a: Athlete): string {
  return (a.firstname?.trim().split(' ') ?? EMPTY_ARRAY)
    .map((str: string) => str.charAt(0))
    .join('')
    .toUpperCase();
}

export function getAthleteAge(a: Athlete): number {
  if (a.birthYear) {
    return new Date().getFullYear() - a.birthYear;
  }
  return 0;
}

export function getAthleteAgeTag(a: Athlete): string {
  const age = getAthleteAge(a);
  if (age <= 20) {
    return 'age:junior';
  } else if (age <= 45) {
    return 'age:adult';
  } else if (age <= 65) {
    return 'age:senior';
  }
  return 'age:elder';
}

export function getAthleteAgeGroup(a: Athlete): string | undefined {
  const age = getAthleteAge(a);
  if (age) {
    switch (true) {
      case age >= 13 && age <= 17:
        return '13-17';
      case age >= 18 && age <= 24:
        return '18-24';
      case age >= 25 && age <= 34:
        return '25-34';
      case age >= 35 && age <= 44:
        return '35-44';
      case age >= 45 && age <= 54:
        return '45-54';
      case age >= 55 && age <= 64:
        return '55-64';
      case age >= 65:
        return '65+';
      default:
        return undefined;
    }
  }
  return undefined;
}

export function getAthleteAllTags(a: Athlete): string[] {
  return [
    ...new Set(
      a.tags.concat([getAthleteAgeTag(a), a.gender === 'FEMALE' ? 'gender:female' : 'gender:male']),
    ),
  ];
}

// ─── Tag helpers (replace methods) ───────────────────────────────────────────

export function hasTagCategory(a: Athlete, tagCategory: string): boolean {
  const search = `${tagCategory}:`;
  return a.tags.findIndex((tag) => tag.startsWith(search)) !== -1;
}

export function getTagValue(a: Athlete, tagCategory: string): string | undefined {
  const search = tagCategory.endsWith(':') ? tagCategory : `${tagCategory}:`;
  const index = a.tags.findIndex((tag) => tag.startsWith(search));
  if (index !== -1) {
    return a.tags[index].split(':').pop();
  }
  return undefined;
}

export function getTag(a: Athlete, tagCategory: string): string | undefined {
  const search = tagCategory.endsWith(':') ? tagCategory : `${tagCategory}:`;
  return a.tags.find((tag) => tag.startsWith(search));
}

export function getTagValues(a: Athlete, tagCategory: string): string[] {
  const search = (tagCategory.startsWith(':') ? tagCategory : `${tagCategory}:`).toLowerCase();
  return a.tags
    .filter((tag) => tag.toLowerCase().startsWith(search))
    .map((tag) => tag.split(':').pop())
    .filter(notUndefined);
}

// ─── Tag mutation helpers (return new tags array — immutable) ────────────────

export function setTagCategory(tags: string[], tagCategory: string, value: string): string[] {
  const search = tagCategory.endsWith(':') ? tagCategory : `${tagCategory}:`;
  const filtered = tags.filter((tag) => !tag.startsWith(search));
  return [...filtered, `${search}${value}`];
}

export function pushTag(tags: string[], tagCategory: string, value: string): string[] {
  const search = `${(tagCategory.endsWith(':') ? tagCategory : `${tagCategory}:`).toLowerCase()}${value}`;
  if (tags.findIndex((tag) => tag === search) === -1) {
    return [...tags, search];
  }
  return tags;
}

export function setTagCategoryValues(tags: string[], tagCategory: string, values: string[]): string[] {
  const cleaned = deleteTagCategory(tags, tagCategory);
  return pushTagCategoryValues(cleaned, tagCategory, values);
}

export function pushTagCategoryValues(tags: string[], tagCategory: string, values: string[]): string[] {
  let result = tags;
  values.forEach((value) => {
    result = pushTag(result, tagCategory, value);
  });
  return result;
}

export function deleteTagCategory(tags: string[], tagCategory: string): string[] {
  const search = (tagCategory.endsWith(':') ? tagCategory : `${tagCategory}:`).toLowerCase();
  return tags.filter((tag) => !tag.toLowerCase().startsWith(search));
}

// ─── Static API methods ─────────────────────────────────────────────────────

export const AthleteApi = {
  findByNickname(nickname: string): Promise<Athlete | undefined> {
    return HttpBackend.get('/athlete/findByNickname', { nickname }).then((res) =>
      res ? (res as Athlete) : undefined,
    );
  },

  checkNickname(nickname: string): Promise<Athlete | undefined> {
    return HttpBackend.get('/athlete/checkNickname', { nickname }, undefined);
  },

  me(): Promise<Athlete> {
    return HttpBackend.get('/athlete/me').then((res) => res as Athlete);
  },
};
